import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as promotionsActions } from 'features/promotions';
import { ROLE_SUPERADMIN } from 'shared/utils/roleBasedLocale';

import { SUPERADMIN_BONUS_GRADES } from './data';

const toNumber = value => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const roundMoney = value => Math.round(toNumber(value) * 100) / 100;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizeGradeId = gradeId => String(gradeId || '').trim().toLowerCase();

const getStaticGradeById = gradeId => {
  const normalizedGradeId = normalizeGradeId(gradeId);

  return SUPERADMIN_BONUS_GRADES.find(grade => grade.id === normalizedGradeId) || SUPERADMIN_BONUS_GRADES[0];
};

const getStaticNextGrade = gradeId => {
  const normalizedGradeId = normalizeGradeId(gradeId);
  const gradeIndex = SUPERADMIN_BONUS_GRADES.findIndex(grade => grade.id === normalizedGradeId);

  return gradeIndex === -1 ? SUPERADMIN_BONUS_GRADES[1] : SUPERADMIN_BONUS_GRADES[gradeIndex + 1] || null;
};

const getGradeWithFallback = grade => {
  if (!grade) return null;

  const staticGrade = getStaticGradeById(grade.id);

  return {
    ...staticGrade,
    ...grade,
    id: normalizeGradeId(grade.id || staticGrade.id),
    name: grade.name || staticGrade.name,
  };
};

const useSuperadminBonusSistem = () => {
  const dispatch = useDispatch();

  const role = useSelector(state => state.auth.role);
  const isVisible = role === ROLE_SUPERADMIN;

  const {
    superadminBonusSistem,
    superadminBonusSistemProcessing,
    superadminBonusSistemLoadFinished,
    superadminBonusSistemLoaded,
  } = useSelector(state => ({
    superadminBonusSistem: state.promotions.superadminBonusSistem,
    superadminBonusSistemProcessing: state.promotions.superadminBonusSistemProcessing,
    superadminBonusSistemLoadFinished: state.promotions.superadminBonusSistemLoadFinished,
    superadminBonusSistemLoaded: state.promotions.superadminBonusSistemLoaded,
  }), shallowEqual);

  useEffect(() => {
    if (!isVisible) return;

    dispatch(promotionsActions.getSuperadminBonusSistem());
  }, [dispatch, isVisible]);

  const {
    period,
    currentGrade: responseCurrentGrade,
    nextGrade: responseNextGrade,
    amountDepositsLast30Days,
    remainingToNextUsdt: responseRemainingToNextUsdt,
    progressToNextLevelPercent,
    paidTransitionGrades,
    updatedAt,
  } = superadminBonusSistem || {};

  return useMemo(() => {
    const currentGrade = getGradeWithFallback(responseCurrentGrade) || SUPERADMIN_BONUS_GRADES[0];

    const fallbackNextGrade = getStaticNextGrade(currentGrade.id);
    const nextGrade = responseNextGrade === null
      ? null
      : getGradeWithFallback(responseNextGrade) || fallbackNextGrade;

    const isMaxGrade = !nextGrade || currentGrade.id === 'brilliant';
    const depositsAmount = roundMoney(amountDepositsLast30Days);
    const remainingToNextUsdt = isMaxGrade ? 0 : roundMoney(responseRemainingToNextUsdt);
    const progressPercent = isMaxGrade ? 100 : clamp(toNumber(progressToNextLevelPercent), 0, 100);
    const gradeIndex = SUPERADMIN_BONUS_GRADES.findIndex(grade => grade.id === currentGrade.id);
    const currentGradeIndex = gradeIndex === -1 ? 0 : gradeIndex;

    return {
      isVisible,
      isLoaded: superadminBonusSistemLoaded,
      isLoading: isVisible && (!superadminBonusSistemLoadFinished || superadminBonusSistemProcessing),
      period,
      updatedAt,

      grades: SUPERADMIN_BONUS_GRADES,
      currentGrade,
      nextGrade,
      currentGradeIndex,
      isMaxGrade,
      paidTransitionGrades,

      amountDepositsLast30Days: depositsAmount,
      progressPercent,
      remainingToNextUsdt,

      progress: {
        percent: progressPercent,
        currentValueUsdt: depositsAmount,
        targetValueUsdt: nextGrade?.minDepositUsdt || currentGrade.nextLevelDepositUsdt,
        remainingToNextUsdt,
      },

      menuWidget: {
        gradeName: currentGrade.name,
        depositBonusPercent: currentGrade.depositBonusPercent,
        progressPercent,
        remainingToNextUsdt,
        isMaxGrade,
      },

      paymentWidget: {
        gradeName: currentGrade.name,
        depositBonusPercent: currentGrade.depositBonusPercent,
        progressPercent,
        remainingToNextUsdt,
        nextGradeName: nextGrade?.name || null,
        nextDepositBonusPercent: nextGrade?.depositBonusPercent || null,
        transitionBonusUsd: nextGrade?.transitionBonusUsd || null,
        isMaxGrade,
      },

      promoPage: {
        grades: SUPERADMIN_BONUS_GRADES,
        currentGrade,
        nextGrade,
        amountDepositsLast30Days: depositsAmount,
        progressPercent,
        remainingToNextUsdt,
        paidTransitionGrades,
        period,
      },
    };
  }, [amountDepositsLast30Days, isVisible, paidTransitionGrades, period, progressToNextLevelPercent, responseCurrentGrade, responseNextGrade, responseRemainingToNextUsdt, superadminBonusSistemLoadFinished, superadminBonusSistemProcessing, superadminBonusSistemLoaded, updatedAt]);
};

export default useSuperadminBonusSistem;